import { useState } from "react";
import "./PeriodFilter.css";

/** 페이지가 보고 있는 기간. 달 하나가 표준이고, 나머지는 그것으로 표현되지 않는 경우다. */
export type PeriodSelection =
  | { type: "all" }
  | { type: "month"; month: string }
  | { type: "recent"; days: number }
  | { type: "custom"; from: string; to: string };

export type MonthRange = { from: string; to: string } | null;

const dayMs = 86_400_000;
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
/** 그 달의 마지막 날. 서버가 실제로 있는 날짜만 받으므로 "9월 31일" 같은 편법은 떨어진다. */
const monthEnd = (month: string) => {
  const [year, index] = month.split("-").map(Number);
  return `${month}-${String(new Date(Date.UTC(year, index, 0)).getUTCDate()).padStart(2, "0")}`;
};

/** 고른 기간을 정산일 범위로 바꾼다. "전체"만 범위가 없다. */
export function periodRange(selection: PeriodSelection, today: string): MonthRange {
  if (selection.type === "all") return null;
  if (selection.type === "month") return { from: `${selection.month}-01`, to: monthEnd(selection.month) };
  if (selection.type === "custom") return { from: selection.from, to: selection.to };
  const end = Date.parse(`${today}T00:00:00Z`);
  return { from: isoDay(end - (selection.days - 1) * dayMs), to: today };
}

/** 지금 고른 기간의 이름. 머리글이 무엇을 보는 중인지 늘 말해야 하므로 밖으로 낸다. */
export function periodLabel(selection: PeriodSelection, today: string) {
  const thisYear = Number(today.slice(0, 4));
  if (selection.type === "all") return "전체";
  if (selection.type === "month") return monthLabel(selection.month, thisYear);
  if (selection.type === "recent") return `최근 ${selection.days}일`;
  return `${selection.from} ~ ${selection.to}`;
}

const monthLabel = (value: string, thisYear: number) =>
  Number(value.slice(0, 4)) === thisYear
    ? `${Number(value.slice(5))}월`
    : `${value.slice(2, 4)}년 ${Number(value.slice(5))}월`;


/**
 * 페이지가 보는 기간을 정하는 한 줄. 달 하나가 표준이라 칩으로 먼저 서고, 그것으로 말할 수
 * 없는 기간 — 여러 달에 걸치거나 주 단위로 보는 추세 — 은 "기간 지정" 뒤에 접어 둔다.
 * 정산 표와 성과 차트가 같은 이 눈금을 보므로, 나간 돈과 성과를 같은 기간으로 맞댈 수 있다.
 */
/**
 * 기간을 정하는 두 칸. 달 칩을 늘어놓았을 때는 여섯 달만 보였고 그보다 앞을 보려면 "기간
 * 지정"을 한 번 더 열어야 했는데, 정작 매번 하는 일은 "이 날부터 이 날까지"였다. 자주 쓰는
 * 눈금도 뺐다 — 날짜 두 칸이면 이번 달도 최근 4주도 같은 손놀림으로 적을 수 있다.
 */
export function PeriodFilter({ value, onChange }: {
  value: PeriodSelection; onChange: (value: PeriodSelection) => void;
}) {
  const [from, setFrom] = useState(value.type === "custom" ? value.from : "");
  const [to, setTo] = useState(value.type === "custom" ? value.to : "");
  /* 둘 다 채워지는 순간 걸린다. "적용"을 따로 누르게 하면 두 칸을 다 채우고도 아무 일이
     일어나지 않아 고장으로 읽힌다. */
  const settle = (nextFrom: string, nextTo: string) => {
    setFrom(nextFrom); setTo(nextTo);
    if (nextFrom && nextTo) onChange({ type: "custom", from: nextFrom, to: nextTo });
  };
  return <div className="period-filter" role="group" aria-label="기간 지정">
    <input type="date" className="period-filter-date" aria-label="시작일" value={from} max={to || undefined}
      onChange={event => settle(event.target.value, to)} data-testid="period-from" />
    <span className="period-filter-tilde" aria-hidden="true">~</span>
    <input type="date" className="period-filter-date" aria-label="종료일" value={to} min={from || undefined}
      onChange={event => settle(from, event.target.value)} data-testid="period-to" />
  </div>;
}
